import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { SERVER_URL } from "@/config/config";
import {
  InboxSendMessageType,
  Message,
  InboxResponse,
} from "@/types/InboxMessage";

export interface InboxState {
  inboxMessages: Message[];
  sentMessages: Message[];
  selectedMessage: Message | null;
  pagination: {
    inbox: {
      page: number;
      pageSize: number;
      total: number;
      totalPages: number;
    };
    sent: {
      page: number;
      pageSize: number;
      total: number;
      totalPages: number;
    };
  };
  loading: {
    inbox: boolean;
    sent: boolean;
    create: boolean;
    reply: boolean;
    read: boolean;
    delete: boolean;
  };
  error: string | null;
}

const initialState: InboxState = {
  inboxMessages: [],
  sentMessages: [],
  selectedMessage: null,
  pagination: {
    inbox: {
      page: 1,
      pageSize: 10,
      total: 0,
      totalPages: 0,
    },
    sent: {
      page: 1,
      pageSize: 10,
      total: 0,
      totalPages: 0,
    },
  },
  loading: {
    inbox: false,
    sent: false,
    create: false,
    reply: false,
    read: false,
    delete: false,
  },
  error: null,
};

// Async thunks
export const getInboxData = createAsyncThunk(
  "inbox/getInboxData",
  async ({
    page = 1,
    pageSize = 20,
    q,
    userId,
    signMsg,
    signature,
  }: {
    page?: number;
    pageSize?: number;
    q?: string;
    userId?: number;
    signMsg: string;
    signature: string;
  }) => {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
    });

    if (q) queryParams.append("q", q);
    if (userId) queryParams.append("userId", userId.toString());

    const response = await axios.get(
      `${SERVER_URL}/message/inbox?${queryParams.toString()}`,
      {
        customHeaders: {
          signMsg: signMsg,
          signature: signature,
        },
      }
    );
    return response.data;
  }
);

export const getSentData = createAsyncThunk(
  "inbox/getSentData",
  async ({
    page = 1,
    pageSize = 20,
    q,
    userId,
    signMsg,
    signature,
  }: {
    page?: number;
    pageSize?: number;
    q?: string;
    userId?: number;
    signMsg: string;
    signature: string;
  }) => {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
    });

    if (q) queryParams.append("q", q);
    if (userId) queryParams.append("userId", userId.toString());

    const response = await axios.get(
      `${SERVER_URL}/message/sent?${queryParams.toString()}`,
      {
        customHeaders: {
          signMsg: signMsg,
          signature: signature,
        },
      }
    );
    return response.data;
  }
);

export const createNewMessage = createAsyncThunk(
  "inbox/createNewMessage",
  async (messageData: InboxSendMessageType) => {
    const response = await axios.post(
      `${SERVER_URL}/message/new`,
      messageData,
      {
        customHeaders: {
          signMsg: messageData.signMsg,
          signature: messageData.signature,
        },
      }
    );
    return response.data;
  }
);

export const replyMessage = createAsyncThunk(
  "inbox/replyMessage",
  async (replyData: {
    user_id: number;
    message_id: string;
    body: string;
    signMsg: string;
    signature: string;
  }) => {
    const response = await axios.post(
      `${SERVER_URL}/message/reply`,
      replyData,
      {
        customHeaders: {
          signMsg: replyData.signMsg,
          signature: replyData.signature,
        },
      }
    );
    return response.data;
  }
);

export const readMessage = createAsyncThunk(
  "inbox/readMessage",
  async ({ messageId, userId }: { messageId: string; userId: number }) => {
    const response = await axios.get(
      `${SERVER_URL}/message/read?messageId=${messageId}&userId=${userId}`
    );
    return response.data;
  }
);

export const deleteMessage = createAsyncThunk(
  "inbox/deleteMessage",
  async ({ messageIds, userId }: { messageIds: number[]; userId: number }) => {
    await axios.post(`${SERVER_URL}/message/delete`, { messageIds, userId });
    return messageIds;
  }
);

// Slice
const inboxSlice = createSlice({
  name: "inbox",
  initialState,
  reducers: {
    setSelectedMessage: (state, action: PayloadAction<Message | null>) => {
      state.selectedMessage = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearMessages: (state) => {
      state.inboxMessages = [];
      state.sentMessages = [];
      state.selectedMessage = null;
    },
  },
  extraReducers: (builder) => {
    // Get inbox data
    builder
      .addCase(getInboxData.pending, (state) => {
        state.loading.inbox = true;
        state.error = null;
      })
      .addCase(getInboxData.fulfilled, (state, action) => {
        state.loading.inbox = false;
        const response = action.payload as InboxResponse;
        state.inboxMessages = response.rows.map((message) => {
          if (message.additional_info) {
            return {
              ...message,
              additionalInfo: JSON.parse(message.additional_info) as {
                address: string;
                familyId: number;
                invitationId?: number;
              },
            };
          }
          return message;
        });
        state.pagination.inbox.total = response.count;
        state.pagination.inbox.totalPages = Math.ceil(
          response.count / state.pagination.inbox.pageSize
        );
        state.pagination.inbox.page = action.meta.arg?.page || 1;
      })
      .addCase(getInboxData.rejected, (state, action) => {
        state.loading.inbox = false;
        state.error = action.payload as string;
      });

    // Get sent data
    builder
      .addCase(getSentData.pending, (state) => {
        state.loading.sent = true;
        state.error = null;
      })
      .addCase(getSentData.fulfilled, (state, action) => {
        state.loading.sent = false;
        const response = action.payload as InboxResponse;
        state.sentMessages = response.rows;
        state.pagination.sent.total = response.count;
        state.pagination.sent.totalPages = Math.ceil(
          response.count / state.pagination.sent.pageSize
        );
        state.pagination.sent.page = action.meta.arg?.page || 1;
      })
      .addCase(getSentData.rejected, (state, action) => {
        state.loading.sent = false;
        state.error = action.payload as string;
      });

    // Create new message
    builder
      .addCase(createNewMessage.pending, (state) => {
        state.loading.create = true;
        state.error = null;
      })
      .addCase(createNewMessage.fulfilled, (state, action) => {
        state.loading.create = false;
        // state.sentMessages.unshift(action.payload);
      })
      .addCase(createNewMessage.rejected, (state, action) => {
        state.loading.create = false;
        state.error = action.payload as string;
      });

    // Reply to message
    builder
      .addCase(replyMessage.pending, (state) => {
        state.loading.reply = true;
        state.error = null;
      })
      .addCase(replyMessage.fulfilled, (state, action) => {
        state.loading.reply = false;
      })
      .addCase(replyMessage.rejected, (state, action) => {
        state.loading.reply = false;
        state.error = action.payload as string;
      });

    // Read message
    builder
      .addCase(readMessage.pending, (state) => {
        state.loading.read = true;
        state.error = null;
      })
      .addCase(readMessage.fulfilled, (state, action) => {
        state.loading.read = false;
        // Update the message's read_at status in inboxMessages
        const messageId = action.meta.arg.messageId;
        const messageIndex = state.inboxMessages.findIndex(
          (message) => message.id.toString() === messageId
        );
        if (messageIndex !== -1) {
          state.inboxMessages[messageIndex].read_at = new Date().toISOString();
        }
      })
      .addCase(readMessage.rejected, (state, action) => {
        state.loading.read = false;
        state.error = action.payload as string;
      });

    // Delete message
    builder
      .addCase(deleteMessage.pending, (state) => {
        state.loading.delete = true;
        state.error = null;
      })
      .addCase(deleteMessage.fulfilled, (state, action) => {
        state.loading.delete = false;
        state.inboxMessages = state.inboxMessages.filter(
          (message) => !action.payload.includes(message.id)
        );
        state.sentMessages = state.sentMessages.filter(
          (message) => !action.payload.includes(message.id)
        );
      })
      .addCase(deleteMessage.rejected, (state, action) => {
        state.loading.delete = false;
        state.error = action.payload as string;
      });
  },
});

export const { setSelectedMessage, clearError, clearMessages } =
  inboxSlice.actions;

export default inboxSlice.reducer;
